import React from "react";
import LegalLayout, { LegalSection, ContactCard } from "./LegalLayout";

const SECTIONS = [
  { id: "admission", title: "1. Admission Policy" },
  { id: "onboarding-fee", title: "2. Onboarding Fee" },
  { id: "scholarship", title: "3. Scholarship Coverage" },
  { id: "enrollment-access", title: "4. Course Enrollment and Access" },
  { id: "deferment", title: "5. Deferment Policy" },
  { id: "refund", title: "6. Refund Policy" },
  { id: "conduct", title: "7. Participant Conduct" },
  { id: "changes", title: "8. Changes to Terms" },
  { id: "contact", title: "9. Contact Information" },
];

function TermsAndCondition() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Terms & Conditions"
      intro="These Terms and Conditions govern enrollment into any program offered by E1X Academy (the “Academy”). By applying to and enrolling in a program, you confirm that you have read, understood, and agreed to these Terms."
      lastUpdated="October 22, 2026"
      sections={SECTIONS}
    >
      <LegalSection id="admission" title="1. Admission Policy">
        <p>
          Admission into an E1X Academy program is provisional upon acceptance of an offer and
          becomes final only once a candidate has completed the full onboarding process, including
          submission of any required information and payment of the onboarding fee.
        </p>
        <p>
          Admission offers are subject to program track and cohort capacity. The Academy reserves
          the right to withdraw an offer if onboarding steps are not completed within the stated
          deadline or if application information is found to be false or misleading.
        </p>
        <p>
          Candidates admitted via scholarship remain subject to the standard onboarding
          requirements unless explicitly stated otherwise in writing.
        </p>
      </LegalSection>

      <LegalSection id="onboarding-fee" title="2. Onboarding Fee">
        <p>
          All enrolling participants (including scholarship recipients) must pay a one-time
          onboarding fee. This fee is strictly separate from and not included in program tuition.
        </p>
        <div className="bg-slate-50 p-5 rounded-xl space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            The fee covers:
          </p>
          <ul className="list-disc list-inside space-y-1.5">
            <li>Program learning materials for the duration of the cohort.</li>
            <li>Access to live interactive sessions, resources, and the learning platform.</li>
            <li>Issuance of an official Certificate of Completion upon finishing.</li>
          </ul>
        </div>
        <p>
          The specific fee will be stated during your onboarding.{" "}
          <strong className="text-slate-800">Partial payments are not accepted</strong> under any
          circumstances.
        </p>
      </LegalSection>

      <LegalSection id="scholarship" title="3. Scholarship Coverage">
        <p>
          Scholarships cover <strong className="text-slate-800">program tuition only</strong>. They
          do not cover or offset the required onboarding fee, which remains the sole responsibility
          of the participant.
        </p>
        <p>
          Acceptance of a scholarship offer does not waive or reduce the onboarding fee unless
          formally documented in writing by E1X Academy.
        </p>
      </LegalSection>

      <LegalSection id="enrollment-access" title="4. Course Enrollment and Access">
        <p>
          Upon successful payment, you will gain access to the enrolled course(s) one week before
          the class resumption date for the duration specified on the course page.
        </p>
        <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-sm">
          Access rights are personal and non-transferable. Sharing your login details or course
          materials with others is strictly prohibited.
        </div>
      </LegalSection>

      <LegalSection id="deferment" title="5. Deferment Policy">
        <p>Participants may request a deferment to a future cohort under the following timeline:</p>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/50">
            <span className="font-semibold text-emerald-800">Within 4 Weeks</span>
            <p className="text-emerald-700 mt-1 text-xs">Processed free of charge.</p>
          </div>
          <div className="p-4 rounded-xl border border-amber-200 bg-amber-50/50">
            <span className="font-semibold text-amber-800">After 3 Weeks</span>
            <p className="text-amber-700 mt-1 text-xs">
              Subject to a 20% administrative fee based on your onboarding payment.
            </p>
          </div>
        </div>
        <p className="text-xs text-slate-500">
          * Note: Scholarships are cohort-specific and do not automatically roll over upon
          deferment.
        </p>
      </LegalSection>

      <LegalSection id="refund" title="6. Refund Policy">
        <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 text-sm">
          <strong>Strict Non-Refundable Policy:</strong> The onboarding fee is non-refundable under
          any circumstance, including program withdrawal, deferment, or dismissal.
        </div>
      </LegalSection>

      <LegalSection id="conduct" title="7. Participant Conduct & Expectations">
        <p>
          Participants must attend live sessions, engage regularly, and complete deliverables on
          time. Misconduct, harassment, plagiarism, or repeated abandonment of work may result in
          immediate suspension or termination without refund.
        </p>
      </LegalSection>

      <LegalSection id="changes" title="8. Changes to These Terms">
        <p>
          E1X Academy reserves the right to amend these terms at any time. Updates will be posted
          to our portal with a revised effective date.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="9. Contact Information">
        <ContactCard>
          For inquiries regarding admissions, onboarding fees, course access, or deferments, email
          our team and we will get back to you.
        </ContactCard>
      </LegalSection>
    </LegalLayout>
  );
}

export default TermsAndCondition;
